import { z } from "zod/v4";
import { type ReadonlyJSONValue, type Transaction } from "@rocicorp/zero";
import { zql } from "../../zero/schema";
import { createId } from "../../utils/id";
import { and, eq } from "drizzle-orm";
import type { WorkflowExecutionStatus, WorkflowExecutionStepStatus } from "./workflow.sql";
import { workflowExecution } from "./workflow.sql";
import {
  isTransitionFunction,
  isFanInTransition,
  type AnyWorkflow,
  type TransitionDefinition,
  type ErrorInfo as DefineErrorInfo,
  type InferWorkflowContext,
  type InferStepInput,
  type InferStepOutput,
} from "../define-workflow";
import { errorInfoSchema as commonErrorInfoSchema } from "../common";

/**
 * Workflow execution boundary for durable runtime orchestration contracts.
 *
 * Defines typed execution/step states plus runtime transition semantics used by
 * server mutators and workflow processors.
 *
 * @see `packages/core/docs/ai/agents.md`
 */
export namespace WorkflowExecution {
  export type Status = Extract<WorkflowExecutionStatus, string>;
  export type StepStatus = Extract<WorkflowExecutionStepStatus, string>;

  export const errorInfoSchema = commonErrorInfoSchema.describe(
    "Workflow execution error information",
  );
  export type ErrorInfo = z.infer<typeof errorInfoSchema>;

  // ============================================================================
  // Query Types (for use with domain-specific workflow relations)
  // ============================================================================

  export type Execution = {
    id: string;
    workflowType: string;
    workflowVersion: number;
    status: Status;
    context: unknown;
    error: unknown;
    metadata: unknown;
    timeCreated: Temporal.Instant;
    steps: readonly ExecutionStep[];
  };

  export type ExecutionStep = {
    id: string;
    executionId: string;
    stepName: string;
    status: StepStatus;
    input: unknown;
    output: unknown;
    error: unknown;
    metadata: unknown;
    retryCount: number | null;
    maxRetries: number | null;
    parentStepId: string | null;
    sortIndex: string;
    timeCreated: Temporal.Instant;
    timeStarted: Temporal.Instant | null;
    timeEnded: Temporal.Instant | null;
  };

  export const getById = (tx: Transaction, executionId: string) => {
    return tx.run(zql.workflowExecution.where("id", "=", executionId).one());
  };

  export const getStepById = (tx: Transaction, stepId: string) => {
    return tx.run(zql.workflowExecutionStep.where("id", "=", stepId).one());
  };

  export const getStepsByExecutionId = (tx: Transaction, executionId: string) => {
    return tx.run(zql.workflowExecutionStep.where("executionId", "=", executionId));
  };

  // ============================================================================
  // Typed Execution (for use with workflow definitions)
  // ============================================================================

  export type Info<Workflow extends AnyWorkflow> = Omit<Execution, "context"> & {
    context: InferWorkflowContext<Workflow>;
  };

  export type StepInfo<
    Workflow extends AnyWorkflow,
    StepName extends keyof Workflow["steps"] & string,
  > = Omit<ExecutionStep, "input" | "output" | "stepName"> & {
    stepName: StepName;
    input: InferStepInput<Workflow["steps"][StepName]>;
    output: InferStepOutput<Workflow["steps"][StepName]> | null;
  };

  export function parse<Workflow extends AnyWorkflow>(
    workflow: Workflow,
    raw: Execution,
  ): Info<Workflow> | null {
    const contextResult = workflow.contextSchema.safeParse(raw.context);
    if (!contextResult.success) {
      return null;
    }
    return { ...raw, context: contextResult.data };
  }

  export function parseStep<
    Workflow extends AnyWorkflow,
    StepName extends keyof Workflow["steps"] & string,
  >(
    workflow: Workflow,
    stepName: StepName,
    raw: ExecutionStep,
  ): StepInfo<Workflow, StepName> | null {
    const step = workflow.steps[stepName];
    if (!step) {
      return null;
    }

    const inputResult = step.inputSchema.safeParse(raw.input);
    if (!inputResult.success) {
      return null;
    }

    const outputResult = raw.output !== null ? step.outputSchema.safeParse(raw.output) : null;
    const output = outputResult === null ? null : outputResult.success ? outputResult.data : null;

    return { ...raw, stepName, input: inputResult.data, output };
  }

  // ============================================================================
  // Registry Type
  // ============================================================================

  export type Registry = Record<string, AnyWorkflow>;

  // ============================================================================
  // Runtime Types
  // ============================================================================

  export type StartOptions<W extends AnyWorkflow, _R extends Registry = Registry> = {
    workflowType: W["workflowType"];
    context: z.infer<W["contextSchema"]>;
    initialStepInput: z.infer<W["steps"][W["initialStep"]]["inputSchema"]>;
    metadata?: Record<string, unknown>;
    initialStepMaxRetries?: number;
  };

  export type StartResult = {
    executionId: string;
    initialStepId: string;
  };

  export type CancelOptions = {
    executionId: string;
    reason?: string;
  };

  export type SpawnStepOptions = {
    executionId: string;
    stepName: string;
    input: unknown;
    parentStepId?: string;
    sortIndex?: string;
    maxRetries?: number;
  };

  export type CompleteStepOptions = {
    stepId: string;
    output: unknown;
  };

  export type FailStepOptions = {
    stepId: string;
    error: ErrorInfo;
    shouldRetry?: boolean;
  };

  export type StartStepOptions = {
    stepId: string;
  };

  export type StepRecord = {
    id: string;
    stepName: string;
    status: WorkflowExecutionStepStatus;
    output: unknown;
    parentStepId: string | null;
  };

  export type FanInReadyResult = {
    stepName: string;
    input: unknown;
    parentStepId: string;
  };

  export type CheckFanInReadinessOptions = {
    executionId: string;
    completedStepId: string;
    completedStepName: string;
    steps: StepRecord[];
  };

  export type RuntimeContext<R extends Registry = Registry> = {
    registry: R;
  };

  // ============================================================================
  // Runtime Interface and Implementation
  // ============================================================================

  export type SetExecutionRunningOptions = {
    executionId: string;
  };

  export type SetExecutionRunningResult = {
    transitioned: boolean;
    previousStatus: WorkflowExecutionStatus;
  };

  export type CheckExecutionCompleteOptions<W extends AnyWorkflow = AnyWorkflow> = {
    executionId: string;
    workflow?: W;
  };

  export type CheckExecutionCompleteResult<PreCommitResult = void> = {
    isComplete: boolean;
    newStatus: WorkflowExecutionStatus | null;
    error?: ErrorInfo;
    preCommitResult?: PreCommitResult;
  };

  export interface Runtime<R extends Registry = Registry> {
    start<WorkflowType extends keyof R & string>(
      tx: Transaction,
      options: StartOptions<R[WorkflowType], R>,
    ): Promise<StartResult>;

    cancel(tx: Transaction, options: CancelOptions): Promise<void>;

    spawnStep(tx: Transaction, options: SpawnStepOptions): Promise<{ stepId: string }>;

    startStep(tx: Transaction, options: StartStepOptions): Promise<void>;

    completeStep(tx: Transaction, options: CompleteStepOptions): Promise<void>;

    failStep(tx: Transaction, options: FailStepOptions): Promise<void>;

    /**
     * Explicitly transition workflow from pending to running.
     * Called when the first step starts processing.
     * This is part of explicit status management - status is not derived from steps.
     */
    setExecutionRunning(
      tx: Transaction,
      options: SetExecutionRunningOptions,
    ): Promise<SetExecutionRunningResult>;

    /**
     * Check if workflow execution is complete and update status if so.
     * Called after step completion to check if all steps are terminal.
     * This is part of explicit status management - status is not derived from steps.
     *
     * If workflow and ctx are provided, preCommit lifecycle hooks will be invoked
     * inside the transaction before returning.
     *
     * Returns the new status if transitioned, null if still running.
     */
    checkExecutionComplete<W extends AnyWorkflow = AnyWorkflow>(
      tx: Transaction,
      options: CheckExecutionCompleteOptions<W>,
    ): Promise<CheckExecutionCompleteResult<unknown>>;

    /**
     * Compute what status would be derived from step states.
     * Used internally by checkExecutionComplete() and for testing.
     * Note: This is a pure function - it doesn't update the database.
     */
    deriveExecutionStatus(
      steps: Array<{ status: WorkflowExecutionStepStatus }>,
    ): WorkflowExecutionStatus;

    evaluateTransitions<W extends AnyWorkflow>(
      workflow: W,
      stepName: keyof W["steps"] & string,
      output: unknown,
    ): Array<{ to: string; input: unknown }> | null;

    checkFanInReadiness<W extends AnyWorkflow>(
      workflow: W,
      options: CheckFanInReadinessOptions,
    ): FanInReadyResult[];
  }

  class RuntimeImpl<R extends Registry> implements Runtime<R> {
    #registry: R;

    constructor(registry: R) {
      this.#registry = registry;
    }

    async start<WorkflowType extends keyof R & string>(
      tx: Transaction,
      options: StartOptions<R[WorkflowType], R>,
    ): Promise<StartResult> {
      const workflow = this.#registry[options.workflowType];
      if (!workflow) {
        throw new Error(`Workflow type "${options.workflowType}" not found in registry`);
      }

      const parsedContext = workflow.contextSchema.parse(options.context);

      const executionId = createId();
      const initialStepId = createId();
      const timeNow = Temporal.Now.instant();

      await tx.mutate.workflowExecution.insert({
        id: executionId,
        workflowType: options.workflowType,
        workflowVersion: workflow.version,
        status: "pending",
        context: parsedContext as ReadonlyJSONValue,
        error: null,
        metadata: (options.metadata as ReadonlyJSONValue | undefined) ?? null,
        timeCreated: timeNow,
      });

      const initialStep = workflow.steps[workflow.initialStep];
      if (!initialStep) {
        throw new Error(
          `Initial step "${workflow.initialStep}" not found in workflow "${options.workflowType}"`,
        );
      }

      const parsedInitialStepInput = initialStep.inputSchema.parse(options.initialStepInput);

      await tx.mutate.workflowExecutionStep.insert({
        id: initialStepId,
        executionId,
        stepName: workflow.initialStep,
        status: "pending",
        input: parsedInitialStepInput as ReadonlyJSONValue,
        output: null,
        error: null,
        metadata: null,
        retryCount: 0,
        maxRetries: options.initialStepMaxRetries ?? 3,
        parentStepId: null,
        sortIndex: "a0",
        timeCreated: timeNow,
        timeStarted: null,
        timeEnded: null,
      });

      return {
        executionId,
        initialStepId,
      };
    }

    async cancel(tx: Transaction, options: CancelOptions): Promise<void> {
      await tx.mutate.workflowExecution.update({
        id: options.executionId,
        status: "canceled",
      });
    }

    async spawnStep(tx: Transaction, options: SpawnStepOptions): Promise<{ stepId: string }> {
      const execution = await tx.run(
        zql.workflowExecution.where("id", "=", options.executionId).one(),
      );
      if (!execution) {
        throw new Error(`Workflow execution "${options.executionId}" not found`);
      }

      const resolvedWorkflow = this.#registry[execution.workflowType];
      if (!resolvedWorkflow) {
        throw new Error(`Workflow type "${execution.workflowType}" not found in registry`);
      }

      const step = resolvedWorkflow.steps[options.stepName];
      if (!step) {
        throw new Error(
          `Step "${options.stepName}" not found in workflow "${resolvedWorkflow.workflowType}"`,
        );
      }

      const parsedInput = step.inputSchema.parse(options.input);

      const stepId = createId();
      const timeNow = Temporal.Now.instant();
      const sortIndex = options.sortIndex ?? stepId;

      await tx.mutate.workflowExecutionStep.insert({
        id: stepId,
        executionId: options.executionId,
        stepName: options.stepName,
        status: "pending",
        input: parsedInput as ReadonlyJSONValue,
        output: null,
        error: null,
        metadata: null,
        retryCount: 0,
        maxRetries: options.maxRetries ?? 3,
        parentStepId: options.parentStepId ?? null,
        sortIndex,
        timeCreated: timeNow,
        timeStarted: null,
        timeEnded: null,
      });

      return { stepId };
    }

    async startStep(tx: Transaction, options: StartStepOptions): Promise<void> {
      const timeNow = Temporal.Now.instant();

      await tx.mutate.workflowExecutionStep.update({
        id: options.stepId,
        status: "running",
        timeStarted: timeNow,
      });
    }

    async completeStep(tx: Transaction, options: CompleteStepOptions): Promise<void> {
      const step = await tx.run(zql.workflowExecutionStep.where("id", "=", options.stepId).one());
      if (!step) {
        throw new Error(`Step "${options.stepId}" not found`);
      }

      const timeNow = Temporal.Now.instant();

      await tx.mutate.workflowExecutionStep.update({
        id: options.stepId,
        status: "completed",
        output: options.output as ReadonlyJSONValue,
        timeStarted: step.timeStarted ?? timeNow,
        timeEnded: timeNow,
      });
    }

    async failStep(tx: Transaction, options: FailStepOptions): Promise<void> {
      const step = await tx.run(zql.workflowExecutionStep.where("id", "=", options.stepId).one());
      if (!step) {
        throw new Error(`Step "${options.stepId}" not found`);
      }

      const timeNow = Temporal.Now.instant();
      const retryCount = step.retryCount ?? 0;
      const maxRetries = step.maxRetries ?? 3;
      const shouldRetry = options.shouldRetry !== false && retryCount < maxRetries;

      if (shouldRetry) {
        await tx.mutate.workflowExecutionStep.update({
          id: options.stepId,
          status: "pending",
          retryCount: retryCount + 1,
          error: options.error,
          timeStarted: null,
        });
      } else {
        await tx.mutate.workflowExecutionStep.update({
          id: options.stepId,
          status: "error",
          error: options.error,
          timeStarted: step.timeStarted ?? timeNow,
          timeEnded: timeNow,
        });
      }
    }

    async setExecutionRunning(
      tx: Transaction,
      options: SetExecutionRunningOptions,
    ): Promise<SetExecutionRunningResult> {
      if (tx.location !== "server") {
        throw new Error("Expected server transaction for workflow execution status update");
      }

      const updatedExecutions = await tx.dbTransaction.wrappedTransaction
        .update(workflowExecution)
        .set({ status: "running" })
        .where(
          and(
            eq(workflowExecution.id, options.executionId),
            eq(workflowExecution.status, "pending"),
          ),
        )
        .returning({ id: workflowExecution.id });

      if (updatedExecutions.length > 0) {
        return { transitioned: true, previousStatus: "pending" };
      }

      const execution = await tx.run(
        zql.workflowExecution.where("id", "=", options.executionId).one(),
      );
      if (!execution) {
        throw new Error(`Workflow execution "${options.executionId}" not found`);
      }

      const previousStatus = execution.status as WorkflowExecutionStatus;

      if (previousStatus === "pending") {
        await tx.mutate.workflowExecution.update({
          id: options.executionId,
          status: "running",
        });
        return { transitioned: true, previousStatus };
      }

      return { transitioned: false, previousStatus };
    }

    async checkExecutionComplete<W extends AnyWorkflow = AnyWorkflow>(
      tx: Transaction,
      options: CheckExecutionCompleteOptions<W>,
    ): Promise<CheckExecutionCompleteResult<unknown>> {
      const execution = await tx.run(
        zql.workflowExecution.where("id", "=", options.executionId).one(),
      );
      if (!execution) {
        throw new Error(`Workflow execution "${options.executionId}" not found`);
      }

      const currentStatus = execution.status as WorkflowExecutionStatus;

      if (
        currentStatus === "completed" ||
        currentStatus === "error" ||
        currentStatus === "canceled"
      ) {
        return { isComplete: true, newStatus: null };
      }

      const steps = await tx.run(
        zql.workflowExecutionStep.where("executionId", "=", options.executionId),
      );

      const derivedStatus = this.deriveExecutionStatus(
        steps.map((s) => ({ status: s.status as WorkflowExecutionStepStatus })),
      );

      const isTerminal =
        derivedStatus === "completed" ||
        derivedStatus === "completed_with_errors" ||
        derivedStatus === "error" ||
        derivedStatus === "canceled";

      if (!isTerminal) {
        return { isComplete: false, newStatus: null };
      }

      let error: ErrorInfo | undefined;

      if (derivedStatus === "error" || derivedStatus === "completed_with_errors") {
        const errorStep = steps.find((s) => s.status === "error" && s.error);
        if (errorStep?.error) {
          const stepError = errorStep.error as ErrorInfo;
          error = {
            message: stepError.message ?? "Unknown error",
            code: stepError.code,
          };
        }
      }

      await tx.mutate.workflowExecution.update({
        id: options.executionId,
        status: derivedStatus,
        error: error,
      });

      let preCommitResult: unknown;

      if (options.workflow) {
        const executionInfo = {
          id: execution.id,
          workflowType: execution.workflowType,
          workflowVersion: execution.workflowVersion,
          context: execution.context,
        };

        if (derivedStatus === "completed" && options.workflow.hooks?.onCompletePreCommit) {
          preCommitResult = await options.workflow.hooks.onCompletePreCommit({
            tx,
            execution: executionInfo,
          });
        } else if (
          (derivedStatus === "error" || derivedStatus === "completed_with_errors") &&
          options.workflow.hooks?.onErrorPreCommit
        ) {
          await options.workflow.hooks.onErrorPreCommit({
            tx,
            execution: executionInfo,
            error: (error ?? { message: "Unknown error" }) as DefineErrorInfo,
          });
        }
      }

      return { isComplete: true, newStatus: derivedStatus, error, preCommitResult };
    }

    deriveExecutionStatus(
      steps: Array<{ status: WorkflowExecutionStepStatus }>,
    ): WorkflowExecutionStatus {
      if (steps.length === 0) {
        return "pending";
      }

      const hasPending = steps.some((s) => s.status === "pending");
      const hasRunning = steps.some((s) => s.status === "running");
      const hasError = steps.some((s) => s.status === "error");
      const hasCanceled = steps.some((s) => s.status === "canceled");
      const allCompleted = steps.every((s) => s.status === "completed");
      const allTerminal = steps.every((s) => ["completed", "error", "canceled"].includes(s.status));

      if (hasPending || hasRunning) {
        return "running";
      }

      if (allCompleted) {
        return "completed";
      }

      if (allTerminal) {
        if (hasError) {
          return "completed_with_errors";
        }
        if (hasCanceled) {
          return "canceled";
        }
        return "completed";
      }

      return "running";
    }

    evaluateTransitions<W extends AnyWorkflow>(
      workflow: W,
      stepName: keyof W["steps"] & string,
      output: unknown,
    ): Array<{ to: string; input: unknown }> | null {
      const transition = workflow.transitions[stepName] as
        | TransitionDefinition<W["steps"], typeof stepName>
        | undefined;

      if (transition === null || transition === undefined) {
        return null;
      }

      if (isTransitionFunction(transition)) {
        const result = (transition as (output: unknown) => unknown)(output);

        if (result === null) {
          return null;
        }

        if (Array.isArray(result)) {
          return result.map((t: { to: string; input: unknown }) => ({
            to: t.to,
            input: t.input,
          }));
        }

        const singleResult = result as { to: string; input: unknown };
        return [{ to: singleResult.to, input: singleResult.input }];
      }

      if (isFanInTransition(transition)) {
        return null;
      }

      return null;
    }

    checkFanInReadiness<W extends AnyWorkflow>(
      workflow: W,
      options: CheckFanInReadinessOptions,
    ): FanInReadyResult[] {
      const { completedStepName, steps } = options;
      const results: FanInReadyResult[] = [];

      for (const [fanInStepName, transition] of Object.entries(workflow.transitions)) {
        if (!isFanInTransition(transition)) {
          continue;
        }

        const { from: fanOutStepName, collect: collectStepName } = transition.awaitFanIn;

        if (completedStepName !== collectStepName) {
          continue;
        }

        const fanOutSteps = steps.filter((s) => s.stepName === fanOutStepName);

        for (const fanOutStep of fanOutSteps) {
          const collectedSteps = steps.filter(
            (s) => s.stepName === collectStepName && s.parentStepId === fanOutStep.id,
          );

          if (collectedSteps.length === 0) {
            continue;
          }

          const allComplete = collectedSteps.every((s) => s.status === "completed");
          if (!allComplete) {
            continue;
          }

          const existingFanInStep = steps.find(
            (s) => s.stepName === fanInStepName && s.parentStepId === fanOutStep.id,
          );
          if (existingFanInStep) {
            continue;
          }

          const outputs = collectedSteps.map((s) => s.output);
          const fanInInput = transition.input(outputs);

          results.push({
            stepName: fanInStepName,
            input: fanInInput,
            parentStepId: fanOutStep.id,
          });
        }
      }

      return results;
    }
  }

  export function createRuntime<R extends Registry>(registry: R): Runtime<R> {
    return new RuntimeImpl(registry);
  }

  export function aggregateStepStatuses(steps: ReadonlyArray<{ status: StepStatus }>): StepStatus {
    if (steps.some((s) => s.status === "running")) return "running";
    if (steps.some((s) => s.status === "pending")) return "pending";
    if (steps.some((s) => s.status === "error")) return "error";
    if (steps.some((s) => s.status === "canceled")) return "canceled";
    if (steps.every((s) => s.status === "completed")) return "completed";
    return "pending";
  }

  export function isTerminalStatus(status: Status): boolean {
    return (
      status === "completed" ||
      status === "completed_with_errors" ||
      status === "error" ||
      status === "canceled"
    );
  }

  export function isActiveStatus(status: Status): boolean {
    return status === "pending" || status === "running";
  }

  // ============================================================================
  // Mutation Schemas
  // ============================================================================

  export const startSchema = z.object({
    workflowType: z.string(),
    input: z.unknown(),
    metadata: z.record(z.string(), z.unknown()).optional(),
  });

  export const cancelSchema = z.object({
    executionId: z.uuid(),
    reason: z.string().optional(),
  });

  // ============================================================================
  // Cancel Mutation
  // ============================================================================

  export type CancelMutationContext = {
    agentRegistry?: unknown;
    actor?: { userId: string };
  };

  export type CancelMutationArgs = z.infer<typeof cancelSchema>;

  export type CancelMutationResult = {
    canceled: boolean;
    runningStepIds: string[];
  };

  export type CancelWorkflowResolver = {
    getWorkflowDefinition: (workflowType: string, version: number) => AnyWorkflow;
  };

  export async function cancel({
    tx,
    ctx,
    args,
    workflowResolver,
  }: {
    tx: Transaction;
    ctx: CancelMutationContext;
    args: CancelMutationArgs;
    workflowResolver: CancelWorkflowResolver;
  }): Promise<CancelMutationResult> {
    const execution = await tx.run(zql.workflowExecution.where("id", "=", args.executionId).one());

    if (!execution) {
      throw new Error(`Workflow execution "${args.executionId}" not found`);
    }

    if (execution.status === "canceled") {
      return { canceled: false, runningStepIds: [] };
    }

    const workflow = workflowResolver.getWorkflowDefinition(
      execution.workflowType,
      execution.workflowVersion,
    );

    const canEdit = await workflow.canEdit({
      tx,
      ctx: ctx as any,
      args: { executionId: args.executionId },
    });

    if (!canEdit) {
      throw new Error("Access denied: Cannot cancel this workflow execution");
    }

    const registry: Registry = { [execution.workflowType]: workflow };
    const runtime = createRuntime(registry);

    await runtime.cancel(tx, {
      executionId: args.executionId,
      reason: args.reason,
    });

    const runningSteps = await tx.run(
      zql.workflowExecutionStep
        .where("executionId", "=", args.executionId)
        .where("status", "=", "running"),
    );

    return {
      canceled: true,
      runningStepIds: runningSteps.map((s) => s.id),
    };
  }
}
